import { apiWithResponseHandle } from 'models/LoadingUtil';
import { createOrUpdateLevel, getLevel, getLevelGlobalSetting, getLevelRules, getLevels, getRetainMembershipTips, updateLevelGlobalSetting, updateLevelRules, updateRetainMembershipTips } from 'services/LevelAPIHelper';
import { createModel } from './BaseModel';
import {
  APIStatus,
  DISPLAY_LEVEL_DOWNGRADE_LOGIC,
  DISPLAY_LEVEL_RENEWAL_LOGIC,
  LanguageConfig,
  LEVEL_CALCULATION_SOURCE,
  LEVEL_RENEWAL_LOGIC,
  MEMBERSHIP_PERIOD_MODE,
  SESSION_KEYS
} from 'config/CustomEnums';
import { getDetailPageDisplayLastModifiedDate, getListDisplayLastModifiedDate } from 'utils/TimeFormatUtil';
import { defaultStep, getNewStepConfig } from './StepBarUtil';
import {
  addDomainToImage,
  getDisplayLastModifiedBy,
  getImageImage,
  removeFromSessionStorage,
  saveToSessionStorage
} from 'utils';

export const sessionDataKey = {
  objectKey: SESSION_KEYS.CREATE_LEVEL_SESSION_KEY,
  stepEndKey: SESSION_KEYS.CREATE_LEVEL_END_SESSION_KEY,
  origionalData: SESSION_KEYS.CREATE_LEVEL_ORIGIN_DATA_SESSION_KEY,
};

const stepNames = ['Content', 'Property'];

export const saveLevelToSessionStorage = (data) => {
  saveToSessionStorage(sessionDataKey.objectKey, data);
};

export const removeLevelSessionStroage = () => {
  removeFromSessionStorage(sessionDataKey.objectKey);
};

const parseRelatedLevel = (data) => {
  if (!data) {
    return null;
  }
  return {
    label: data?.levelName,
    value: data,
  }
}

const parseLevel = (item) => {
  const entryRequirements = [];
  if (item.enableCheckTargetTotalPointsEarned) {
    entryRequirements.push(
      item.levelCalculationSource === LEVEL_CALCULATION_SOURCE.pp
        ? `Earn ${item.targetTotalPointsEarned} PP`
        : `Spend ${item.targetTotalPointsEarned} HK$`
    )
  }
  if (item.enableCheckAccumulatedSpendingInAPeriod) {
    entryRequirements.push(
      `${item.accumulatedSpendingInThePeriod} HK$ (in ${item.accumulatedSpendingInXDay} days)`
    )
  }
  if (item.enableCheckSpendingInOnePurchase) {
    entryRequirements.push(
      `${item.spendingInOnePurchase} HK$ (single purchase)`
    )
  }

  const retainRequirements = [];
  if (item.enableCheckCalculationSourceValueWithinPeriodForRetain) {
    retainRequirements.push(
      item.levelCalculationSource === LEVEL_CALCULATION_SOURCE.pp
        ? `Earn ${item.retainRequiredValue} PP`
        : `Spend ${item.retainRequiredValue} HK$`
    )
  }
  if (item.enableCheckEffectiveTransactionTimesWithinPeriod) {
    retainRequirements.push(
      `${item.requiredTransactionTimes} times of transaction(s) and each at least ${item.minimumAmountOfOneTransactionForRenew} HK$`
    )
  }

  let displayDowngradeLevel = null;
  if (item.downgradeLogic === LEVEL_RENEWAL_LOGIC.forceDowngrade) {
    displayDowngradeLevel = item.renewForceDowngradeLevel?.levelName;
  }

  let displayMinimumDowngradeLevel = null;
  if (item.downgradeLogic === LEVEL_RENEWAL_LOGIC.checkRetainRequirement) {
    displayDowngradeLevel = item.minimumDowngradeLevel?.levelName;
  }

  const translationsEdges = item.translations?.edges;
  let translations = {};
  translations[LanguageConfig.english] = {
    language: LanguageConfig.english,
    levelName: item.levelName,
  }
  if (translationsEdges?.length > 0) {
    translationsEdges.forEach((item) => {
      const node = item.node;
      translations[node.language] = node;
    });
  }

  return {
    ...item,
    name: item.levelName,
    displayForegroundElementColor: item.foregroundElementColor?.replace("#", ""),
    displayMembershipCardBackgroundColor: item.membershipCardBackgroundColor?.replace("#", ""),
    cardLandscapeBackgroundImageInApp: addDomainToImage(item.cardLandscapeBackgroundImageInApp),
    cardPortraitBackgroundImageInApp: addDomainToImage(item.cardPortraitBackgroundImageInApp),
    cardLandscapeBackgroundImageInWeb: addDomainToImage(item.cardLandscapeBackgroundImageInWeb),
    cardPortraitBackgroundImageInWeb: addDomainToImage(item.cardPortraitBackgroundImageInWeb),
    displayEntryRequirements: entryRequirements.join(' or\n'),
    displayRenewalLogic: DISPLAY_LEVEL_RENEWAL_LOGIC[item.renewLogic],
    displayRetainRequirements: retainRequirements.join(' or \n'),
    displayDowngradeLogic: DISPLAY_LEVEL_DOWNGRADE_LOGIC[item.downgradeLogic],
    displayDowngradeLevel,
    displayMinimumDowngradeLevel,
    dropdownDowngradeLevel: parseRelatedLevel(item.renewForceDowngradeLevel),
    dropdownMinimumDowngradeLevel: parseRelatedLevel(item.minimumDowngradeLevel),
    displayLastModifiedDate: getListDisplayLastModifiedDate(item.lastModifiedDate),
    detailDisplayCreationDate: getDetailPageDisplayLastModifiedDate(item.creationDate),
    detailDisplayLastModifiedDate: getDetailPageDisplayLastModifiedDate(item.lastModifiedDate),
    displayLastModifiedBy: getDisplayLastModifiedBy(item),
    displayStatus: item.active ? 'Active' : 'Deactive',
    translations,
  };
};

const parseLevelList = (data) => {
  return data.map((item) => {
    return parseLevel(item.node);
  });
};

const parseUpgradeLevelList = (data) => {
  let upgradeLevelList = [];
  for (var i = 1; i < data.length; i++) {
    upgradeLevelList.push({
      name: data[i - 1].node.levelName + ' to ' + data[i].node.levelName,
      id: data[i].node.id,
      pk: data[i].node.pk,
      levelRank: data[i].node.levelRank,
      label: data[i - 1].node.levelName + ' to ' + data[i].node.levelName,
    });
  }
  return upgradeLevelList;
};


export const cleanTranslations = (data, language, fields = []) => {
  const languageField = {};
  const translation = data?.translations?.[language] || {};
  if (language !== LanguageConfig.english) {
    languageField["language"] = language;

    if (translation.pk) {
      languageField['id'] = translation.pk;
    }
  }
  fields.forEach(item => {
    languageField[item] = translation?.[item] || null;
  })
  return languageField;
}

const cleanLevelData = (data) => {
  const formattedTranslation = cleanTranslations(
    data,
    LanguageConfig.english,
    ['levelName'],
  );

  const otherTranslations = [];
  Object.keys(data?.translations || {}).forEach((language) => {
    if (language === LanguageConfig.english) {
      return;
    }
    otherTranslations.push(
      cleanTranslations(data, language, ['levelName']),
    );
  });

  const renewLogic = Object.keys(DISPLAY_LEVEL_RENEWAL_LOGIC).find(
    key => DISPLAY_LEVEL_RENEWAL_LOGIC[key] === data.displayRenewalLogic
  );

  const downgradeLogic = Object.keys(DISPLAY_LEVEL_DOWNGRADE_LOGIC).find(
    key => DISPLAY_LEVEL_DOWNGRADE_LOGIC[key] === data.displayDowngradeLogic
  );

  const result = {
    active: data.active || false,
    foregroundElementColor: `#${data.displayForegroundElementColor}`,
    membershipCardBackgroundType: data.membershipCardBackgroundType,
    membershipCardBackgroundColor: data.displayMembershipCardBackgroundColor ? `#${data.displayMembershipCardBackgroundColor}` : null,
    cardLandscapeBackgroundImageInApp: getImageImage(data.cardLandscapeBackgroundImageInApp) || null,
    cardPortraitBackgroundImageInApp: getImageImage(data.cardPortraitBackgroundImageInApp) || null,
    cardLandscapeBackgroundImageInWeb: getImageImage(data.cardLandscapeBackgroundImageInWeb) || null,
    cardPortraitBackgroundImageInWeb: getImageImage(data.cardPortraitBackgroundImageInWeb) || null,
    hideLevelDetailsAndUpgradeTips: data.hideLevelDetailsAndUpgradeTips || false,
    isLowestLevel: data.isLowestLevel || false,
    levelRank: data.levelRank,
    enableCheckTargetTotalPointsEarned: data.enableCheckTargetTotalPointsEarned || false,
    targetTotalPointsEarned: data.targetTotalPointsEarned || null,
    enableCheckAccumulatedSpendingInAPeriod: data.enableCheckAccumulatedSpendingInAPeriod || false,
    accumulatedSpendingInThePeriod: data.accumulatedSpendingInThePeriod || null,
    accumulatedSpendingInXDay: data.accumulatedSpendingInXDay || null,
    enableCheckSpendingInOnePurchase: data.enableCheckSpendingInOnePurchase || false,
    spendingInOnePurchase: data.spendingInOnePurchase || null,
    renewLogic: renewLogic || null,
    enableCheckCalculationSourceValueWithinPeriodForRetain: data.enableCheckCalculationSourceValueWithinPeriodForRetain || false,
    retainRequiredValue: data.retainRequiredValue || null,
    enableCheckEffectiveTransactionTimesWithinPeriod: data.enableCheckEffectiveTransactionTimesWithinPeriod || false,
    requiredTransactionTimes: data.requiredTransactionTimes || null,
    minimumAmountOfOneTransactionForRenew: data.minimumAmountOfOneTransactionForRenew || null,
    downgradeLogic: downgradeLogic || null,
    minimumDowngradeLevel: data.dropdownMinimumDowngradeLevel?.value?.pk || null,
    renewForceDowngradeLevel: data.dropdownDowngradeLevel?.value?.pk || null,
    translations: otherTranslations,
    ...formattedTranslation,
  };
  if (data.pk) {
    result["id"] = data.pk;
  }

  return result;
}

const parseSingleLevelRule = (item) => {
  const translationsEdges = item.translations?.edges;
  let translations = {};
  translations[LanguageConfig.english] = {
    language: LanguageConfig.english,
    title: item.title,
    content: item.content,
  }
  if (translationsEdges?.length > 0) {
    translationsEdges.forEach((item) => {
      const node = item.node;
      translations[node.language] = node;
    });
  }

  return {
    id: item.pk,
    pk: item.pk,
    title: item.title,
    content: item.content,
    displayOrder: item.displayOrder,
    translations,
  };
};

const parseLevelRules = (data) => {
  if (!data) {
    return [];
  };
  return data.map((item) => {
    return parseSingleLevelRule(item.node);
  });
}

const cleanLevelRules = (data) => {
  if (!data) {
    return [];
  }

  return data?.map((item, index) => {
    const formattedTranslation = cleanTranslations(
      item,
      LanguageConfig.english,
      ['title', 'content']
    );

    const otherTranslations = [];
    Object.keys(item?.translations || {}).forEach((language) => {
      if (language === LanguageConfig.english) {
        return;
      }
      otherTranslations.push(
        cleanTranslations(item, language, ['title', 'content']),
      );
    });

    const result = {
      displayOrder: index + 1,
      translations: otherTranslations,
      ...formattedTranslation,
    }

    if (item.pk) {
      result["id"] = item.pk;
    }

    return result;
  })
}

const parseRetainMembershipTips = (data) => {
  if (!data) {
    return {};
  };

  const tips = data?.[0]?.node || {};

  const translationsEdges = tips.translations?.edges;
  let translations = {};
  translations[LanguageConfig.english] = {
    language: LanguageConfig.english,
    title: tips.title,
    description: tips.description,
  }
  if (translationsEdges?.length > 0) {
    translationsEdges.forEach((item) => {
      const node = item.node;
      translations[node.language] = node;
    });
  }
  return {
    id: tips.pk,
    pk: tips.pk,
    title: tips.title,
    description: tips.description,
    translations,
  }
}

const cleanRetainMembershipTips = (data) => {
  if (!data) {
    return {};
  }

  const formattedTranslation = cleanTranslations(
    data,
    LanguageConfig.english,
    ['title', 'description']
  );

  const otherTranslations = [];
  Object.keys(data?.translations || {}).forEach((language) => {
    if (language === LanguageConfig.english) {
      return;
    }
    otherTranslations.push(
      cleanTranslations(data, language, ['title', 'description']),
    );
  });

  const result = {
    translations: otherTranslations,
    ...formattedTranslation,
  }

  if (data.pk) {
    result["id"] = data.pk;
  }

  return result;
}

const parseLevelGlobalSetting = (data) => {
  if (!data) {
    return {};
  };

  const setting = data?.[0]?.node || {};

  const fixedPeriodStartDatetime = setting.fixedPeriodStartDatetime;
  let fixedPeriodStartMonth = null;
  let fixedPeriodStartDay = null;
  if (fixedPeriodStartDatetime) {
    const startDate = new Date(fixedPeriodStartDatetime);
    fixedPeriodStartMonth = startDate.getMonth() + 1;
    fixedPeriodStartDay = startDate.getDate();
  }

  return {
    id: setting.pk,
    pk: setting.pk,
    membershipPeriodMode: setting.membershipPeriodMode,
    fixedPeriodStartMonth,
    fixedPeriodStartDay,
    dynamicDuration: setting.dynamicDuration,
    extendMembershipOnUpgrade: setting.extendMembershipOnUpgrade,
    levelCalculationSource: setting.levelCalculationSource,
    handlingCalculationSourceAfterRenewal: setting.handlingCalculationSourceAfterRenewal,
    enableEasyUpgrade: setting.enableEasyUpgrade,
    handlingExpiredPeriodXDayAfterRefund: setting.handlingExpiredPeriodXDayAfterRefund,
  }
}

const cleanLevelGlobalSetting = (data) => {
  if (!data) {
    return {};
  }

  let fixedPeriodStartDatetime = null;
  if (data.fixedPeriodStartMonth && data.fixedPeriodStartDay) {
    fixedPeriodStartDatetime = new Date();
    fixedPeriodStartDatetime.setMonth(data.fixedPeriodStartMonth, data.fixedPeriodStartDay);
    fixedPeriodStartDatetime.setHours(23, 59, 59, 0);
  }

  let dynamicDuration = data.dynamicDuration;
  let handlingExpiredPeriodXDayAfterRefund = data.handlingExpiredPeriodXDayAfterRefund;
  if (data.membershipPeriodMode === MEMBERSHIP_PERIOD_MODE.fixed) {
    dynamicDuration = 365;
    handlingExpiredPeriodXDayAfterRefund = 0;
  }

  const result = {
    id: data.pk,
    membershipPeriodMode: data.membershipPeriodMode,
    fixedPeriodStartDatetime,
    dynamicDuration,
    extendMembershipOnUpgrade: data.extendMembershipOnUpgrade,
    levelCalculationSource: data.levelCalculationSource,
    handlingCalculationSourceAfterRenewal: data.handlingCalculationSourceAfterRenewal,
    enableEasyUpgrade: data.enableEasyUpgrade,
    handlingExpiredPeriodXDayAfterRefund,
  }

  return result;
}

const getInitialState = () => ({
  listDisplayFields: [
    { displayName: 'ID', fieldName: 'pk', orderField: 'pk', linked: true },
    { displayName: 'Level name', fieldName: 'name', linked: false },
    { displayName: 'Level number', fieldName: 'levelRank', orderField: 'levelRank' },
    { displayName: 'Entry requirements', fieldName: 'displayEntryRequirements'},
    { displayName: 'Renewal Logic', fieldName: 'displayRenewalLogic' },
    { displayName: 'Downgrade Logic', fieldName: 'displayDowngradeLogic' },
    { displayName: 'Downgrade To', fieldName: 'displayDowngradeLevel' },
    { displayName: 'Last modified', fieldName: 'displayLastModifiedDate' },
    { displayName: 'Status', fieldName: 'displayStatus' },
  ],
  levelList: [],
  stepConfig: defaultStep(stepNames),
  currentStep: 0,
  createLevelStatus: APIStatus.none,

  levelRulesLoading: false,
  levelRulesList: [],

  retainMembershipTipsLoading: false,
  retainMembershipTips: {},

  levelGlobalSettingLoading: false,
  levelGlobalSetting: {},
});

export default createModel({
  namespace: 'levels',
  states: getInitialState(),

  reducers: {
    stepChange(state, { payload }) {
      const isBack = payload.isBack;
      let step = payload.step;
      const isValid = payload.isValid;
      const stepConfig = getNewStepConfig(step, state.stepConfig, !isValid, isBack);

      if (isValid) {
        step = isBack ? step - 1 : step + 1;
      }
      return {
        ...state,
        currentStep: step,
        stepConfig,
        createLevelStatus: APIStatus.none,
      };
    },
  },

  params: {
    initState: {
      detail: {
        displayForegroundElementColor: "3E3E3E",
        displayMembershipCardBackgroundColor: "4E8DD7",
      }
    },
    sessionKey: SESSION_KEYS.LEVEL_SESSION_KEY,
    dataKey: SESSION_KEYS.LEVEL_DATA_SESSION_KEY,
    listAPI: getLevels,
    parse: (data) => data?.levels.edges.map((item) => parseLevel(item.node)),
    pkNode: 'LevelNode',
    detailAPI: getLevel,
    parseDetail: (data) => parseLevel(data.level),
    objectKey: SESSION_KEYS.LEVEL_DATA_SESSION_KEY,
  },

  effects: {
    getLevelList: [
      function* ({ payload }, { call, select, put }) {
        const serviceArgs = [getLevels, null, {}];
        function* onSuccess(data) {
          const levelData = data?.levels.edges || [];
          const levelDataOrdered = levelData.sort((a, b) => a.node.levelRank - b.node.levelRank)
          const levelList = parseLevelList(levelData);
          const upgradeLevels = parseUpgradeLevelList(levelDataOrdered);
          yield put({
            type: 'updateState',
            payload: {
              levelList,
              upgradeLevels,
            },
          });
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],

    createOrUpdateLevel: [
      function* ({ payload }, { call, select, put, all }) {
        yield put({
          type: 'updateState',
          payload: { createLevelStatus: APIStatus.calling },
        });
        saveToSessionStorage(sessionDataKey.stepEndKey, true);
        console.log('@@create or update level payload', payload);
        const originalLevel = payload?.data || {};
        const levelData = cleanLevelData(originalLevel);
        console.log('@@levelData', levelData);
        const serviceArgs = [createOrUpdateLevel, levelData];

        function* onSuccess(data) {
          removeLevelSessionStroage();
          console.log('@@create or update level data', data);

          yield put({
            type: 'updateState',
            payload: {
              createLevelStatus: APIStatus.success,
              formHasSubmitted: true,
            },
          })

          if (payload?.afterAction) {
            payload.afterAction();
          };
        }
        function* failed(data) {
          console.log('create or update level failed:', data);
          yield put({
            type: 'updateState',
            payload: {
              createLevelStatus: APIStatus.failed,
            },
          });
        }

        yield apiWithResponseHandle(serviceArgs, onSuccess, failed, failed);
      },
      { type: 'takeLatest' },
    ],

    updateLevelStatus: [
      function* ({ payload }, { call, select, put, all }) {
        console.log("@@updateLevelStatus", payload);

        const levelData = {
          id: payload?.pk,
          levelName: payload?.levelName,
          active: payload?.active,
        };
        const serviceArgs = [createOrUpdateLevel, levelData];

        function onSuccess(data) {
          console.log('@@updateLevelStatus onSuccess:', data);
          if (payload?.afterAction) {
            payload.afterAction();
          };
        }
        function failed(data) {
          console.log('@@updateLevelStatus failed:', data);
        }

        yield apiWithResponseHandle(serviceArgs, onSuccess, failed, failed);
      },
      { type: 'takeLatest' },
    ],

    getLevelRules: [
      function* ({ payload }, { call, select, put }) {
        yield put({
          type: 'updateState',
          payload: {
            levelRulesLoading: true,
          }
        })

        const serviceArgs = [getLevelRules];
        function* onSuccess(data) {
          const levelRulesList = parseLevelRules(data?.levelRules.edges);
          yield put({
            type: 'updateState',
            payload: {
              levelRulesList,
              levelRulesLoading: false,
            },
          });
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],

    updateLevelRules: [
      function* ({ payload }, { call, select, put }) {
        const levelRulesList = cleanLevelRules(payload?.data?.levelRules);
        const updatePayload = {
          levelRules: levelRulesList,
        };
        const serviceArgs = [updateLevelRules, updatePayload];
        function onSuccess(data) {
          console.log("@@updateLevelRules", data);

          if (payload?.afterAction) {
            payload.afterAction();
          }
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],

    getRetainMembershipTips: [
      function* ({ payload }, { call, select, put }) {
        yield put({
          type: 'updateState',
          payload: {
            retainMembershipTipsLoading: true,
          }
        })

        const serviceArgs = [getRetainMembershipTips];
        function* onSuccess(data) {
          const retainMembershipTips = parseRetainMembershipTips(data?.retainMembershipTips.edges);
          yield put({
            type: 'updateState',
            payload: {
              retainMembershipTips,
              retainMembershipTipsLoading: false,
            },
          });
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],

    updateRetainMembershipTips: [
      function* ({ payload }, { call, select, put }) {
        const updatePayload = cleanRetainMembershipTips(payload?.data);
        const serviceArgs = [updateRetainMembershipTips, updatePayload];
        function onSuccess(data) {
          console.log("@@updateRetainMembershipTips", data);

          if (payload?.afterAction) {
            payload.afterAction();
          }
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],

    getLevelGlobalSetting: [
      function* ({ payload }, { call, select, put }) {
        yield put({
          type: 'updateState',
          payload: {
            levelGlobalSettingLoading: true,
          }
        })

        const serviceArgs = [getLevelGlobalSetting];
        function* onSuccess(data) {
          const levelGlobalSetting = parseLevelGlobalSetting(data?.levelGlobalSettings.edges);
          yield put({
            type: 'updateState',
            payload: {
              levelGlobalSetting,
              levelGlobalSettingLoading: false,
            },
          });
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],

    updateLevelGlobalSetting: [
      function* ({ payload }, { call, select, put }) {
        const updatePayload = cleanLevelGlobalSetting(payload?.data);
        const serviceArgs = [updateLevelGlobalSetting, updatePayload];
        function onSuccess(data) {
          console.log("@@updateLevelGlobalSetting", data);

          if (payload?.afterAction) {
            payload.afterAction();
          }
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
  },
});
